import { CONFIG } from '../../config';

interface SoundManagerLike {
    unlockAudio(): void;
    playBGM(): void;
}

/**
 * 게임패드 입력 처리
 */
export class GamepadInput {
    private soundManager: SoundManagerLike | null;
    private gamepad: Gamepad | null;
    private previousButtons: Record<string | number, boolean>;
    private audioUnlocked: boolean;
    private connectedHandler: ((e: GamepadEvent) => void) | null = null;
    private disconnectedHandler: (() => void) | null = null;

    constructor(soundManager: SoundManagerLike | null = null) {
        this.soundManager = soundManager;
        this.gamepad = null;
        this.previousButtons = {};
        this.audioUnlocked = false;
        this.setup();
    }

    setup(): void {
        this.connectedHandler = (e: GamepadEvent) => {
            console.log('Gamepad connected:', e.gamepad.id);
        };

        this.disconnectedHandler = () => {
            console.log('Gamepad disconnected');
            this.audioUnlocked = false;
        };

        window.addEventListener('gamepadconnected', this.connectedHandler);
        window.addEventListener('gamepaddisconnected', this.disconnectedHandler);
    }

    destroy(): void {
        if (this.connectedHandler) {
            window.removeEventListener('gamepadconnected', this.connectedHandler);
            this.connectedHandler = null;
        }
        if (this.disconnectedHandler) {
            window.removeEventListener('gamepaddisconnected', this.disconnectedHandler);
            this.disconnectedHandler = null;
        }
        this.gamepad = null;
        this.previousButtons = {};
    }

    update(): void {
        const gamepads = navigator.getGamepads ? navigator.getGamepads() : [];
        this.gamepad = null;

        for (const gp of gamepads) {
            if (gp && gp.connected) {
                this.gamepad = gp;

                // Unlock audio on first button press
                if (!this.audioUnlocked && this.soundManager) {
                    const anyButtonPressed = gp.buttons.some(button => button.pressed);
                    if (anyButtonPressed) {
                        this.soundManager.unlockAudio();
                        this.soundManager.playBGM();
                        this.audioUnlocked = true;
                    }
                }
                break;
            }
        }
    }

    getAxis(axisName: 'horizontal' | 'vertical'): number {
        if (!this.gamepad) return 0;

        let value = 0;
        const deadzone = CONFIG.INPUT.GAMEPAD.DEADZONE;

        if (axisName === 'horizontal') {
            const stickX = this.gamepad.axes[0];
            if (Math.abs(stickX) > deadzone) {
                value = stickX;
            }
            // D-pad
            if (this.gamepad.buttons[14]?.pressed) value -= 1;
            if (this.gamepad.buttons[15]?.pressed) value += 1;
        } else if (axisName === 'vertical') {
            const stickY = this.gamepad.axes[1];
            if (Math.abs(stickY) > deadzone) {
                value = stickY;
            }
            // D-pad
            if (this.gamepad.buttons[12]?.pressed) value -= 1;
            if (this.gamepad.buttons[13]?.pressed) value += 1;
        }

        return Math.max(-1, Math.min(1, value));
    }

    isButtonJustPressed(buttonIndex: number): boolean {
        if (!this.gamepad || !this.gamepad.buttons[buttonIndex]) return false;

        const button = this.gamepad.buttons[buttonIndex];
        const wasPressed = this.previousButtons[buttonIndex] || false;
        const justPressed = button.pressed && !wasPressed;
        this.previousButtons[buttonIndex] = button.pressed;

        return justPressed;
    }

    isConfirmPressed(): boolean {
        return this.isButtonJustPressed(CONFIG.INPUT.GAMEPAD.BUTTON_CONFIRM);
    }

    isCancelPressed(): boolean {
        return this.isButtonJustPressed(CONFIG.INPUT.GAMEPAD.BUTTON_CANCEL);
    }

    isUpPressed(): boolean {
        // D-pad Up (button 12) 또는 스틱 위
        if (this.isButtonJustPressed(12)) return true;
        if (!this.gamepad) return false;
        const stickY = this.gamepad.axes[1];
        const wasUp = this.previousButtons['stickUp'] || false;
        const isUp = stickY < -0.5;
        const justUp = isUp && !wasUp;
        this.previousButtons['stickUp'] = isUp;
        return justUp;
    }

    isDownPressed(): boolean {
        // D-pad Down (button 13) 또는 스틱 아래
        if (this.isButtonJustPressed(13)) return true;
        if (!this.gamepad) return false;
        const stickY = this.gamepad.axes[1];
        const wasDown = this.previousButtons['stickDown'] || false;
        const isDown = stickY > 0.5;
        const justDown = isDown && !wasDown;
        this.previousButtons['stickDown'] = isDown;
        return justDown;
    }

    isLeftPressed(): boolean {
        // D-pad Left (button 14) 또는 스틱 왼쪽
        if (this.isButtonJustPressed(14)) return true;
        if (!this.gamepad) return false;
        const stickX = this.gamepad.axes[0];
        const wasLeft = this.previousButtons['stickLeft'] || false;
        const isLeft = stickX < -0.5;
        const justLeft = isLeft && !wasLeft;
        this.previousButtons['stickLeft'] = isLeft;
        return justLeft;
    }

    isRightPressed(): boolean {
        // D-pad Right (button 15) 또는 스틱 오른쪽
        if (this.isButtonJustPressed(15)) return true;
        if (!this.gamepad) return false;
        const stickX = this.gamepad.axes[0];
        const wasRight = this.previousButtons['stickRight'] || false;
        const isRight = stickX > 0.5;
        const justRight = isRight && !wasRight;
        this.previousButtons['stickRight'] = isRight;
        return justRight;
    }
}
